import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, CircularProgress, Stack } from '@mui/material'

import errorHandlerHook from '@hooks/useErrorHandler'

import BlogPostService from '@services/blog-post'

import { Carousel } from '@pages/LandingPage/components/Carousel'

import { TLGTypography } from '@components/TLGTypography'

import { IBlogPost } from '@utils/types'

import {
  carouselItemsWrapperSx,
  circularProgressSx,
  containerSx,
  contentSx,
  titleSx,
} from './styles'

import { leftArrowWrapperSx, rightArrowWrapperSx } from '../Carousel/styles'
import { BlogPostCard } from './components/BlogPostCard'

const SIZE_OF_ITEMS_PER_WINDOW_BREAKPOINT_IN_PX = {
  xs: 254,
  md: 309,
  lg: 360,
}

const LatestBlogPosts = () => {
  const { t } = useTranslation()

  const [recentBlogPosts, setRecentBlogPosts] = useState<IBlogPost[]>()
  const [isLoadingRecentBlogPosts, setIsLoadingRecentBlogPosts] =
    useState(false)

  const { handleError } = errorHandlerHook.useErrorHandler()

  const fetchRecentBlogPosts = useCallback(async () => {
    setIsLoadingRecentBlogPosts(true)

    try {
      const { data } = await BlogPostService.findAllAsVisitor({
        queryFilters: { page: 1, limit: 12 },
      })

      setRecentBlogPosts(data)
    } catch (err) {
      handleError({ exception: err })
    }

    setIsLoadingRecentBlogPosts(false)
  }, [handleError])

  useEffect(() => {
    fetchRecentBlogPosts()
  }, [fetchRecentBlogPosts])

  if (!recentBlogPosts?.length) {
    return null
  }

  return (
    <Stack data-testid="latest-blog-posts" sx={containerSx}>
      {isLoadingRecentBlogPosts ? (
        <CircularProgress sx={circularProgressSx} />
      ) : (
        <>
          <TLGTypography
            responsiveVariant={{
              xs: 'h5',
              md: 'h4',
            }}
            sx={titleSx}
          >
            {t('Latest blog posts')}
          </TLGTypography>
          <Box sx={contentSx}>
            <Carousel
              id="latest-blog-posts-carousel"
              whiteArrows
              maxItemsPerSlide={3}
              itemsWrapperSx={carouselItemsWrapperSx}
              leftArrowWrapperSx={leftArrowWrapperSx}
              rightArrowWrapperSx={rightArrowWrapperSx}
              numberOfItemsPerPage={{
                dynamic: true,
                size: SIZE_OF_ITEMS_PER_WINDOW_BREAKPOINT_IN_PX,
              }}
              items={recentBlogPosts ?? []}
              renderItem={(blogPost: IBlogPost) => (
                <BlogPostCard
                  data-testid="this-one"
                  key={blogPost.slug}
                  title={blogPost.title}
                  type={blogPost.type}
                  slug={blogPost.slug}
                  preview={blogPost.preview}
                  image={blogPost.image as string}
                />
              )}
            />
          </Box>
        </>
      )}
    </Stack>
  )
}

export { LatestBlogPosts }
